/* eslint-disable max-lines */
/**
 * Demo resources for Care page UI prototype
 * This file contains demonstration data for the CCM (Connected Community Model) interface
 * Will be replaced with real data from entities collection when backend integration is complete
 *
 * Entity types: person, organization, program, community
 * Images: Unsplash free stock photos
 */

// Unsplash image URLs for demo (free to use)
const IMAGES = {
  // People - professional headshots
  sarahChen: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop&crop=face',
  marcusJohnson: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face',
  drEmilyWatts: 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=400&h=400&fit=crop&crop=face',
  alexRivera: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face',
  jamesWorthington: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop&crop=face',

  // Organizations - office/building imagery
  growthOps: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=400&fit=crop',
  talentBridge: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=400&h=400&fit=crop',
  impactVentures: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400&h=400&fit=crop',
  lighterCapital: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=400&fit=crop',
  enterpriseConnect: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=400&fit=crop',
  utInnovation: 'https://images.unsplash.com/photo-1562774053-701939374585?w=400&h=400&fit=crop',

  // Programs - abstract/wellness imagery
  mindfulExec: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=400&fit=crop',
  resilienceRetreat: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400&h=400&fit=crop',
  sbirProgram: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=400&fit=crop',

  // Communities - group/networking imagery
  foundersCircle: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&h=400&fit=crop',
  techCouncil: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=400&fit=crop',
  saasAlliance: 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400&h=400&fit=crop',
};

// CCM resources organized by section - entities that can help the selected person
export const CCM_RESOURCES = {
  // Capacity section - people/orgs that can help with team, skills, operations
  capacity: [
    {
      id: 'entity-sarah-chen',
      entityType: 'person',
      name: 'Sarah Chen',
      title: 'Fractional CTO',
      organization: 'TechBridge Partners',
      avatar: IMAGES.sarahChen,
      relevanceTag: 'Tech Leadership',
      relevanceDetail: 'Has scaled 3 startups from seed to Series B. Expertise in building engineering teams.',
      matchScore: 95,
    },
    {
      id: 'entity-growth-ops',
      entityType: 'organization',
      name: 'GrowthOps Collective',
      title: 'Operations Consultancy',
      organization: null,
      avatar: IMAGES.growthOps,
      relevanceTag: 'Operations',
      relevanceDetail: 'Specializes in systematizing operations for growing startups. 40+ client transformations.',
      matchScore: 88,
    },
    {
      id: 'entity-marcus-johnson',
      entityType: 'person',
      name: 'Marcus Johnson',
      title: 'Leadership Coach',
      organization: 'Executive Edge',
      avatar: IMAGES.marcusJohnson,
      relevanceTag: 'Executive Coaching',
      relevanceDetail: 'Former VP at Fortune 500. Coaches founders on leadership presence and team building.',
      matchScore: 82,
    },
    {
      id: 'entity-talent-bridge',
      entityType: 'organization',
      name: 'TalentBridge',
      title: 'Executive Recruiting',
      organization: null,
      avatar: IMAGES.talentBridge,
      relevanceTag: 'Hiring',
      relevanceDetail: 'Specializes in placing C-suite and VP-level talent at growth-stage companies.',
      matchScore: 79,
    },
  ],

  // Care section - wellness, mental health, personal development resources
  care: [
    {
      id: 'entity-mindful-exec',
      entityType: 'program',
      name: 'Mindful Executive',
      title: '8-Week Program',
      organization: 'Faana Works',
      avatar: IMAGES.mindfulExec,
      relevanceTag: 'Stress Management',
      relevanceDetail: 'Evidence-based program combining neuroscience and mindfulness for founder wellness.',
      matchScore: 94,
    },
    {
      id: 'entity-dr-emily-watts',
      entityType: 'person',
      name: 'Dr. Emily Watts',
      title: 'Executive Therapist',
      organization: 'Founder Wellness Center',
      avatar: IMAGES.drEmilyWatts,
      relevanceTag: 'Mental Health',
      relevanceDetail: 'Specializes in founder burnout and anxiety. Confidential telehealth available.',
      matchScore: 91,
    },
    {
      id: 'entity-founders-circle',
      entityType: 'community',
      name: "Founders' Circle",
      title: 'Peer Support Group',
      organization: 'Faana Network',
      avatar: IMAGES.foundersCircle,
      relevanceTag: 'Peer Support',
      relevanceDetail: 'Monthly facilitated sessions with 8-10 founders at similar stages. Safe space to share.',
      matchScore: 87,
    },
    {
      id: 'entity-resilience-retreat',
      entityType: 'program',
      name: 'Resilience Retreat',
      title: '3-Day Intensive',
      organization: 'Mountain Mind Institute',
      avatar: IMAGES.resilienceRetreat,
      relevanceTag: 'Restoration',
      relevanceDetail: 'Quarterly retreats focused on renewal, reflection, and strategic clarity.',
      matchScore: 75,
    },
  ],

  // Capital section - funding, financial resources, investors
  capital: [
    {
      id: 'entity-impact-ventures',
      entityType: 'organization',
      name: 'Impact Ventures Fund',
      title: 'Series A Investor',
      organization: null,
      avatar: IMAGES.impactVentures,
      relevanceTag: 'Series A Ready',
      relevanceDetail: '$50M fund focused on mission-driven B2B SaaS. Average check size $2-5M.',
      matchScore: 92,
    },
    {
      id: 'entity-alex-rivera',
      entityType: 'person',
      name: 'Alex Rivera',
      title: 'Angel Investor',
      organization: 'Rivera Family Office',
      avatar: IMAGES.alexRivera,
      relevanceTag: 'Bridge Financing',
      relevanceDetail: 'Active angel with 40+ investments. Writes $50-250K checks. Quick decisions.',
      matchScore: 89,
    },
    {
      id: 'entity-lighter-capital',
      entityType: 'organization',
      name: 'Lighter Capital',
      title: 'Revenue-Based Financing',
      organization: null,
      avatar: IMAGES.lighterCapital,
      relevanceTag: 'Non-Dilutive',
      relevanceDetail: 'Revenue-based financing up to $3M. No equity, no board seats, founder-friendly.',
      matchScore: 85,
    },
    {
      id: 'entity-james-worthington',
      entityType: 'person',
      name: 'James Worthington III',
      title: 'Strategic Investor',
      organization: 'Worthington Holdings',
      avatar: IMAGES.jamesWorthington,
      relevanceTag: 'Strategic Capital',
      relevanceDetail: 'Family office with focus on sustainable businesses. Patient capital, long-term view.',
      matchScore: 78,
    },
    {
      id: 'entity-sbir-program',
      entityType: 'program',
      name: 'SBIR Phase II',
      title: 'Government Grant',
      organization: 'NSF',
      avatar: IMAGES.sbirProgram,
      relevanceTag: 'Grant Funding',
      relevanceDetail: 'Up to $1M in non-dilutive funding. Application deadline in 45 days.',
      matchScore: 71,
    },
  ],

  // Community Model section - networks, partnerships, ecosystems
  communityModel: [
    {
      id: 'entity-tech-council',
      entityType: 'community',
      name: 'Austin Tech Council',
      title: 'Regional Network',
      organization: null,
      avatar: IMAGES.techCouncil,
      relevanceTag: 'Local Network',
      relevanceDetail: '500+ member organization. Monthly events, introductions to local investors and talent.',
      matchScore: 90,
    },
    {
      id: 'entity-saas-alliance',
      entityType: 'community',
      name: 'B2B SaaS Alliance',
      title: 'Industry Group',
      organization: null,
      avatar: IMAGES.saasAlliance,
      relevanceTag: 'Industry Peers',
      relevanceDetail: 'Exclusive group of 200 B2B SaaS founders. Private Slack, quarterly dinners.',
      matchScore: 86,
    },
    {
      id: 'entity-enterprise-partners',
      entityType: 'organization',
      name: 'Enterprise Connect',
      title: 'Channel Partnership',
      organization: null,
      avatar: IMAGES.enterpriseConnect,
      relevanceTag: 'Sales Channel',
      relevanceDetail: 'Reseller network with 50+ enterprise accounts. Potential warm introductions.',
      matchScore: 83,
    },
    {
      id: 'entity-university-lab',
      entityType: 'organization',
      name: 'UT Innovation Lab',
      title: 'Research Partnership',
      organization: 'University of Texas',
      avatar: IMAGES.utInnovation,
      relevanceTag: 'R&D Partner',
      relevanceDetail: 'Access to research talent, grants, and emerging technology. Startup-friendly terms.',
      matchScore: 76,
    },
  ],
};

// Complete player card data for ALL entities
export const PLAYER_CARDS = {
  // Default fallback for any entity without specific player card data
  'default': {
    snapshot: {
      name: 'Entity Name',
      title: 'Title',
      organization: 'Organization',
      quickBio: 'This is a placeholder bio. Full player card data is being developed for this entity.',
      photoUrl: null,
    },
    vision: {
      betterPlace: 'Vision content is being developed.',
      aimToChange: 'What we aim to change is being developed.',
      purpose: 'Our purpose is being developed.',
      uniqueApproach: 'Our unique approach is being developed.',
    },
    company: {
      name: 'Company Name',
      tagline: 'Tagline coming soon',
      industry: 'Industry',
      founded: '-',
      headquarters: '-',
      stage: '-',
      coreOffering: 'Core offering details are being developed.',
      targetMarket: 'Target market details are being developed.',
    },
    highlights: {
      revenue: '-',
      customers: '-',
      partnerships: '-',
      milestones: '-',
      awards: '-',
      metrics: {
        arrMrr: '-',
        customerCount: '-',
        retention: '-',
        growthRate: '-',
        fundingRaised: '-',
      },
      funding: {
        totalRaised: '-',
        currentRaise: '-',
        useOfFunds: '-',
      },
    },
    entityType: 'organization',
  },

  // ============ CAPACITY SECTION ============
  'entity-sarah-chen': {
    snapshot: {
      name: 'Sarah Chen',
      title: 'Fractional CTO',
      organization: 'TechBridge Partners',
      quickBio: 'Sarah Chen is a seasoned technology executive who has built and scaled engineering teams at three successful startups. With expertise in modern cloud architecture and team leadership, she helps founders build technical foundations that support rapid growth. Previously VP Engineering at DataScale (acquired by Salesforce) and founding engineer at CloudSync.',
      photoUrl: IMAGES.sarahChen,
    },
    vision: {
      betterPlace: 'A world where every founder has access to world-class technical leadership, regardless of their ability to afford a full-time CTO.',
      aimToChange: 'The outdated model of expensive full-time executive hires that drain runway and create misaligned incentives.',
      purpose: 'TechBridge exists to democratize access to senior technical leadership. We believe great technology leadership should be accessible to all growth-stage companies.',
      uniqueApproach: 'We combine fractional engagement with a "teach to fish" methodology - building internal capabilities while providing hands-on leadership.',
    },
    company: {
      name: 'TechBridge Partners',
      tagline: 'Fractional CTOs for Growth-Stage Startups',
      industry: 'Technology Consulting',
      founded: '2019',
      headquarters: 'Austin, TX',
      stage: 'Established',
      coreOffering: 'Fractional CTO services including technical strategy, team building, architecture review, and hands-on engineering leadership.',
      targetMarket: 'Series A to Series C funded startups needing senior technical leadership without full-time commitment.',
    },
    highlights: {
      revenue: '$2.4M',
      customers: '24',
      partnerships: '8',
      milestones: '15+',
      awards: '3',
      metrics: {
        arrMrr: '$200K MRR',
        customerCount: '24 active clients',
        retention: '95%',
        growthRate: '40% YoY',
        fundingRaised: 'Bootstrapped',
      },
      funding: {
        totalRaised: 'N/A - Profitable',
        currentRaise: 'Not raising',
        useOfFunds: 'Organic growth through reinvestment',
      },
    },
    entityType: 'person',
  },

  'entity-growth-ops': {
    snapshot: {
      name: 'GrowthOps Collective',
      title: 'Operations Consultancy',
      organization: null,
      quickBio: 'GrowthOps Collective is a boutique operations consultancy founded by former COOs from high-growth startups. We specialize in helping companies systematize their operations during critical growth phases, turning chaos into scalable processes. Our team has collectively helped 40+ companies navigate the transition from scrappy startup to organized scale-up.',
      photoUrl: IMAGES.growthOps,
    },
    vision: {
      betterPlace: 'A world where operational excellence is not a luxury reserved for large enterprises, but a foundational capability accessible to every growing company.',
      aimToChange: 'The reactive, firefighting approach to operations that causes so many promising startups to stumble during their growth phase.',
      purpose: 'GrowthOps exists to give founders their time back by building operational systems that run without constant intervention.',
      uniqueApproach: 'We embed with your team for 90-day sprints, building systems alongside your people rather than creating dependency on external consultants.',
    },
    company: {
      name: 'GrowthOps Collective',
      tagline: 'Systematize Your Growth',
      industry: 'Management Consulting',
      founded: '2020',
      headquarters: 'Denver, CO',
      stage: 'Growth',
      coreOffering: '90-day operational transformation sprints covering hiring, onboarding, financial operations, and team coordination systems.',
      targetMarket: 'Venture-backed startups with 20-100 employees experiencing growing pains and operational chaos.',
    },
    highlights: {
      revenue: '$1.8M',
      customers: '42',
      partnerships: '12',
      milestones: '40+',
      awards: '2',
      metrics: {
        arrMrr: '$150K MRR',
        customerCount: '42 transformations',
        retention: '88%',
        growthRate: '55% YoY',
        fundingRaised: 'Bootstrapped',
      },
      funding: {
        totalRaised: 'Self-funded',
        currentRaise: 'Not raising',
        useOfFunds: 'Team expansion and methodology development',
      },
    },
    entityType: 'organization',
  },

  'entity-marcus-johnson': {
    snapshot: {
      name: 'Marcus Johnson',
      title: 'Leadership Coach',
      organization: 'Executive Edge',
      quickBio: 'Marcus Johnson spent 15 years climbing the corporate ladder at Fortune 500 companies before discovering his true calling: helping founders develop into the leaders their teams need. As a certified executive coach with an MBA from Wharton, Marcus combines business acumen with deep expertise in leadership development and organizational psychology.',
      photoUrl: IMAGES.marcusJohnson,
    },
    vision: {
      betterPlace: 'A world where every founder has the leadership skills to build companies that bring out the best in their people.',
      aimToChange: 'The myth that leadership is an innate talent rather than a learnable skill set that can be developed through deliberate practice.',
      purpose: 'Executive Edge exists to accelerate founder leadership development, helping them avoid the painful lessons that derail so many promising companies.',
      uniqueApproach: 'We combine 1:1 coaching with real-time shadowing, giving founders feedback in the moments that matter most.',
    },
    company: {
      name: 'Executive Edge',
      tagline: 'Leadership Development for Founders',
      industry: 'Executive Coaching',
      founded: '2018',
      headquarters: 'Chicago, IL',
      stage: 'Established',
      coreOffering: 'Executive coaching programs for founders including 1:1 sessions, leadership assessments, team dynamics workshops, and board presentation coaching.',
      targetMarket: 'First-time founders of venture-backed companies who are growing into CEO roles.',
    },
    highlights: {
      revenue: '$850K',
      customers: '35',
      partnerships: '5',
      milestones: '200+',
      awards: '4',
      metrics: {
        arrMrr: '$70K MRR',
        customerCount: '35 active clients',
        retention: '92%',
        growthRate: '30% YoY',
        fundingRaised: 'Bootstrapped',
      },
      funding: {
        totalRaised: 'Self-funded',
        currentRaise: 'Not raising',
        useOfFunds: 'Online course development',
      },
    },
    entityType: 'person',
  },

  'entity-talent-bridge': {
    snapshot: {
      name: 'TalentBridge',
      title: 'Executive Recruiting',
      organization: null,
      quickBio: 'TalentBridge is an executive search firm exclusively focused on placing senior leaders at growth-stage technology companies. Founded by former startup operators who understand the unique challenges of hiring at scale, we have placed over 200 executives who have collectively helped their companies raise $2B+ in follow-on funding.',
      photoUrl: IMAGES.talentBridge,
    },
    vision: {
      betterPlace: 'A world where the best executive talent flows to the most innovative companies, regardless of brand recognition or recruiting budget.',
      aimToChange: 'The broken executive search model that prioritizes placements over fit, leaving startups with expensive mis-hires.',
      purpose: 'TalentBridge exists to give startups access to the same caliber of executive talent that Fortune 500 companies attract.',
      uniqueApproach: 'We guarantee cultural fit with a 12-month replacement guarantee and use proprietary assessments validated across 200+ placements.',
    },
    company: {
      name: 'TalentBridge',
      tagline: 'Executive Search for Growth-Stage Companies',
      industry: 'Executive Recruiting',
      founded: '2017',
      headquarters: 'San Francisco, CA',
      stage: 'Established',
      coreOffering: 'Retained executive search for C-suite and VP-level roles, including candidate sourcing, assessment, and onboarding support.',
      targetMarket: 'Series A to Series D technology companies hiring their first or replacement executives.',
    },
    highlights: {
      revenue: '$4.2M',
      customers: '85',
      partnerships: '20',
      milestones: '200+',
      awards: '3',
      metrics: {
        arrMrr: 'Project-based',
        customerCount: '85 client companies',
        retention: '94%',
        growthRate: '35% YoY',
        fundingRaised: '$2M Seed',
      },
      funding: {
        totalRaised: '$2M Seed',
        currentRaise: 'Not raising',
        useOfFunds: 'AI-powered candidate matching platform',
      },
    },
    entityType: 'organization',
  },

  // ============ CARE SECTION ============
  'entity-mindful-exec': {
    snapshot: {
      name: 'Mindful Executive',
      title: '8-Week Program',
      organization: 'Faana Works',
      quickBio: 'Mindful Executive is an evidence-based wellness program designed specifically for founders and executives. Developed in partnership with neuroscientists at Stanford, the 8-week program combines mindfulness practices with practical tools for stress management, emotional regulation, and sustainable high performance. Over 500 founders have completed the program.',
      photoUrl: IMAGES.mindfulExec,
    },
    vision: {
      betterPlace: 'A world where high performance and personal wellbeing are complementary rather than competing priorities.',
      aimToChange: 'The glorification of burnout culture in startups and the false belief that self-care is incompatible with building a successful company.',
      purpose: 'Mindful Executive exists to give founders the mental tools to sustain excellence over the long journey of company building.',
      uniqueApproach: 'We combine ancient mindfulness practices with modern neuroscience, delivered in a format designed for busy executives with measurable outcomes.',
    },
    company: {
      name: 'Faana Works - Mindful Executive Program',
      tagline: 'Sustainable High Performance for Founders',
      industry: 'Executive Wellness',
      founded: '2021',
      headquarters: 'Austin, TX',
      stage: 'Growth',
      coreOffering: '8-week cohort-based program including daily practices, weekly group sessions, 1:1 coaching, and a companion app for habit building.',
      targetMarket: 'Founders and C-suite executives at high-growth companies seeking sustainable performance without burnout.',
    },
    highlights: {
      revenue: '$420K',
      customers: '500+',
      partnerships: '8',
      milestones: '12',
      awards: '2',
      metrics: {
        arrMrr: '$35K MRR',
        customerCount: '500+ alumni',
        retention: '89%',
        growthRate: '80% YoY',
        fundingRaised: 'Part of Faana',
      },
      funding: {
        totalRaised: 'Internal program',
        currentRaise: 'N/A',
        useOfFunds: 'Program expansion and research',
      },
    },
    entityType: 'program',
  },

  'entity-dr-emily-watts': {
    snapshot: {
      name: 'Dr. Emily Watts',
      title: 'Executive Therapist',
      organization: 'Founder Wellness Center',
      quickBio: 'Dr. Emily Watts is a licensed clinical psychologist who specializes in working with entrepreneurs and executives. After spending a decade in traditional practice, she founded the Founder Wellness Center to address the unique mental health challenges faced by startup founders. She holds a PhD from UCLA and has treated over 300 founders.',
      photoUrl: IMAGES.drEmilyWatts,
    },
    vision: {
      betterPlace: 'A world where seeking mental health support is seen as a sign of strength and self-awareness, not weakness, especially among high achievers.',
      aimToChange: 'The stigma around mental health in the startup community that prevents founders from getting the support they need.',
      purpose: 'The Founder Wellness Center exists to provide confidential, expert mental health support tailored to the unique pressures of entrepreneurship.',
      uniqueApproach: 'We offer flexible telehealth sessions designed around founder schedules, with expertise in the specific challenges of startup life.',
    },
    company: {
      name: 'Founder Wellness Center',
      tagline: 'Mental Health Support for Entrepreneurs',
      industry: 'Mental Health Services',
      founded: '2019',
      headquarters: 'Los Angeles, CA',
      stage: 'Established',
      coreOffering: 'Individual therapy, couples counseling for founder relationships, crisis support, and group therapy programs for entrepreneurs.',
      targetMarket: 'Founders, executives, and their partners seeking confidential mental health support from someone who understands startup life.',
    },
    highlights: {
      revenue: '$1.2M',
      customers: '300+',
      partnerships: '15',
      milestones: '5',
      awards: '3',
      metrics: {
        arrMrr: '$100K MRR',
        customerCount: '300+ clients served',
        retention: '91%',
        growthRate: '45% YoY',
        fundingRaised: 'Bootstrapped',
      },
      funding: {
        totalRaised: 'Self-funded',
        currentRaise: 'Not raising',
        useOfFunds: 'Expanding therapist network',
      },
    },
    entityType: 'person',
  },

  'entity-founders-circle': {
    snapshot: {
      name: "Founders' Circle",
      title: 'Peer Support Group',
      organization: 'Faana Network',
      quickBio: "Founders' Circle is a facilitated peer support program designed specifically for early to growth-stage founders. Groups of 8-10 founders meet monthly in confidential settings to share challenges, celebrate wins, and support each other through the founder journey. Each circle is facilitated by an experienced coach who ensures productive, meaningful conversations.",
      photoUrl: IMAGES.foundersCircle,
    },
    vision: {
      betterPlace: 'A world where no founder feels alone - where every entrepreneur has a trusted circle of peers who truly understand their journey.',
      aimToChange: 'The isolation epidemic among founders. Studies show 70% of founders report feeling lonely, leading to burnout and poor decision-making.',
      purpose: "Founders' Circle exists to create spaces of authentic connection and mutual support for founders navigating the challenges of building companies.",
      uniqueApproach: 'We combine peer support with professional facilitation, ensuring productive conversations while maintaining the authenticity of peer connection.',
    },
    company: {
      name: "Founders' Circle",
      tagline: 'Peer Support for the Founder Journey',
      industry: 'Founder Wellness',
      founded: '2022',
      headquarters: 'Austin, TX',
      stage: 'Growth',
      coreOffering: 'Monthly facilitated peer support sessions with curated groups of 8-10 founders at similar stages, plus async community support.',
      targetMarket: 'Founders of companies with $500K-$10M in revenue who value authentic peer connection and personal growth.',
    },
    highlights: {
      revenue: '$180K',
      customers: '48',
      partnerships: '6',
      milestones: '12',
      awards: '1',
      metrics: {
        arrMrr: '$15K MRR',
        customerCount: '48 members',
        retention: '92%',
        growthRate: '60% YoY',
        fundingRaised: 'Bootstrapped',
      },
      funding: {
        totalRaised: 'N/A - Bootstrapped',
        currentRaise: 'Not raising',
        useOfFunds: 'Expansion to 3 new cities',
      },
    },
    entityType: 'community',
  },

  'entity-resilience-retreat': {
    snapshot: {
      name: 'Resilience Retreat',
      title: '3-Day Intensive',
      organization: 'Mountain Mind Institute',
      quickBio: 'Resilience Retreat is a quarterly 3-day immersive experience held in the mountains of Colorado, designed to help founders step back from the daily grind and reconnect with their purpose. Combining wilderness experiences, facilitated reflection, and peer connection, the retreat has helped over 200 founders return to their companies with renewed clarity and energy.',
      photoUrl: IMAGES.resilienceRetreat,
    },
    vision: {
      betterPlace: 'A world where founders regularly invest in their own renewal, understanding that sustainable leadership requires intentional restoration.',
      aimToChange: 'The always-on culture that prevents founders from ever fully disconnecting and recharging.',
      purpose: 'Resilience Retreat exists to provide founders with transformative experiences that restore energy, provide clarity, and build lasting connections.',
      uniqueApproach: 'We combine wilderness immersion with structured reflection and peer connection in a completely device-free environment.',
    },
    company: {
      name: 'Mountain Mind Institute',
      tagline: 'Restoration for Founders',
      industry: 'Executive Retreats',
      founded: '2020',
      headquarters: 'Boulder, CO',
      stage: 'Growth',
      coreOffering: 'Quarterly 3-day retreats including wilderness experiences, facilitated workshops, 1:1 coaching sessions, and peer dinners.',
      targetMarket: 'Founders and executives seeking deep restoration and strategic clarity through immersive retreat experiences.',
    },
    highlights: {
      revenue: '$650K',
      customers: '200+',
      partnerships: '8',
      milestones: '16',
      awards: '2',
      metrics: {
        arrMrr: 'Seasonal',
        customerCount: '200+ alumni',
        retention: '85%',
        growthRate: '70% YoY',
        fundingRaised: 'Bootstrapped',
      },
      funding: {
        totalRaised: 'Self-funded',
        currentRaise: 'Not raising',
        useOfFunds: 'New retreat location development',
      },
    },
    entityType: 'program',
  },

  // ============ CAPITAL SECTION ============
  'entity-impact-ventures': {
    snapshot: {
      name: 'Impact Ventures Fund',
      title: 'Series A Investor',
      organization: null,
      quickBio: 'Impact Ventures is a $50M fund focused on backing mission-driven B2B SaaS companies at the Series A stage. Founded by former operators who understand the founder journey, we bring capital, connections, and hands-on support to help portfolio companies achieve breakout growth. Our portfolio has created over 500 jobs and generated measurable positive impact.',
      photoUrl: IMAGES.impactVentures,
    },
    vision: {
      betterPlace: 'A future where profitable businesses and positive impact are inseparable - where the best returns come from solving the most important problems.',
      aimToChange: 'The false dichotomy between profit and purpose. Too many investors force founders to choose between doing well and doing good.',
      purpose: 'Impact Ventures exists to prove that backing mission-driven founders creates superior returns while making the world better.',
      uniqueApproach: 'We only invest in businesses where impact and growth are structurally aligned. Our portfolio construction ensures diversification across impact themes.',
    },
    company: {
      name: 'Impact Ventures Fund',
      tagline: 'Backing Mission-Driven Founders',
      industry: 'Venture Capital',
      founded: '2021',
      headquarters: 'San Francisco, CA',
      stage: 'Fund II',
      coreOffering: 'Series A investments of $2-5M in B2B SaaS companies with clear mission alignment and path to profitability.',
      targetMarket: 'B2B SaaS founders with $1-3M ARR, strong unit economics, and verifiable impact metrics.',
    },
    highlights: {
      revenue: 'N/A',
      customers: '12',
      partnerships: '25',
      milestones: '8',
      awards: '2',
      metrics: {
        arrMrr: 'N/A',
        customerCount: '12 portfolio companies',
        retention: '100%',
        growthRate: 'N/A',
        fundingRaised: '$50M AUM',
      },
      funding: {
        totalRaised: '$50M Fund I',
        currentRaise: 'Fund II - $75M target',
        useOfFunds: '15-20 Series A investments',
      },
    },
    entityType: 'organization',
  },

  'entity-alex-rivera': {
    snapshot: {
      name: 'Alex Rivera',
      title: 'Angel Investor',
      organization: 'Rivera Family Office',
      quickBio: 'Alex Rivera is an active angel investor who has deployed over $8M across 40+ early-stage companies. A former founder himself (sold his e-commerce company in 2018), Alex brings operational experience alongside capital. Known for quick decisions and founder-friendly terms, he focuses on B2B software and marketplaces.',
      photoUrl: IMAGES.alexRivera,
    },
    vision: {
      betterPlace: 'A world where promising founders can access early capital quickly without jumping through endless hoops.',
      aimToChange: 'The slow, bureaucratic fundraising process that wastes founder time and energy during the most critical phase of company building.',
      purpose: 'I invest to help founders skip the mistakes I made and accelerate their path to product-market fit.',
      uniqueApproach: 'I make investment decisions within one week, provide founder-friendly terms, and stay available for tactical advice without being overbearing.',
    },
    company: {
      name: 'Rivera Family Office',
      tagline: 'Fast, Founder-Friendly Angel Capital',
      industry: 'Angel Investing',
      founded: '2019',
      headquarters: 'Miami, FL',
      stage: 'Active',
      coreOffering: 'Angel investments of $50K-$250K in pre-seed and seed stage B2B software and marketplace companies.',
      targetMarket: 'First-time and repeat founders building B2B software with early traction and clear path to revenue.',
    },
    highlights: {
      revenue: 'N/A',
      customers: '42',
      partnerships: '10',
      milestones: '8',
      awards: '1',
      metrics: {
        arrMrr: 'N/A',
        customerCount: '42 portfolio companies',
        retention: 'N/A',
        growthRate: 'N/A',
        fundingRaised: '$8M deployed',
      },
      funding: {
        totalRaised: 'N/A',
        currentRaise: 'Evergreen',
        useOfFunds: 'Ongoing angel investments',
      },
    },
    entityType: 'person',
  },

  'entity-lighter-capital': {
    snapshot: {
      name: 'Lighter Capital',
      title: 'Revenue-Based Financing',
      organization: null,
      quickBio: 'Lighter Capital is the largest provider of revenue-based financing for technology companies, having deployed over $350M to 500+ companies. Our non-dilutive financing allows founders to access growth capital without giving up equity or board seats. Perfect for companies with recurring revenue seeking to extend runway between equity rounds.',
      photoUrl: IMAGES.lighterCapital,
    },
    vision: {
      betterPlace: 'A world where founders have diverse financing options and can choose the capital structure that best fits their goals.',
      aimToChange: 'The assumption that equity financing is the only path to growth for technology companies.',
      purpose: 'Lighter Capital exists to provide founders with non-dilutive capital options that let them maintain ownership and control.',
      uniqueApproach: 'Revenue-based financing with no personal guarantees, no warrants, and flexible repayment tied to your actual revenue.',
    },
    company: {
      name: 'Lighter Capital',
      tagline: 'Non-Dilutive Growth Financing',
      industry: 'Alternative Lending',
      founded: '2010',
      headquarters: 'Seattle, WA',
      stage: 'Established',
      coreOffering: 'Revenue-based financing from $50K to $3M for technology companies with recurring revenue.',
      targetMarket: 'B2B SaaS and technology companies with $15K+ MRR seeking non-dilutive growth capital.',
    },
    highlights: {
      revenue: '$25M+',
      customers: '500+',
      partnerships: '50+',
      milestones: '100+',
      awards: '5',
      metrics: {
        arrMrr: 'N/A',
        customerCount: '500+ companies funded',
        retention: 'N/A',
        growthRate: '40% YoY',
        fundingRaised: '$350M+ deployed',
      },
      funding: {
        totalRaised: '$100M+ credit facilities',
        currentRaise: 'N/A',
        useOfFunds: 'Funding technology companies',
      },
    },
    entityType: 'organization',
  },

  'entity-james-worthington': {
    snapshot: {
      name: 'James Worthington III',
      title: 'Strategic Investor',
      organization: 'Worthington Holdings',
      quickBio: 'James Worthington III manages the investment arm of the Worthington family office, focusing on sustainable businesses with long-term growth potential. Unlike traditional VCs, James takes a patient capital approach with 10+ year time horizons. He brings deep industry connections in manufacturing, logistics, and enterprise software.',
      photoUrl: IMAGES.jamesWorthington,
    },
    vision: {
      betterPlace: 'A world where more investors think in decades rather than quarters, supporting founders who are building for lasting impact.',
      aimToChange: 'The short-term thinking that pervades venture capital and pressures founders into unsustainable growth.',
      purpose: 'Worthington Holdings invests to support founders building enduring companies that create value for all stakeholders.',
      uniqueApproach: 'We offer patient capital with no artificial timeline pressure, combined with access to our family network across industry and finance.',
    },
    company: {
      name: 'Worthington Holdings',
      tagline: 'Patient Capital for Enduring Companies',
      industry: 'Family Office',
      founded: '1985',
      headquarters: 'Boston, MA',
      stage: 'Established',
      coreOffering: 'Growth equity investments of $1-10M in sustainable businesses with proven business models and long-term growth potential.',
      targetMarket: 'Profitable or near-profitable companies seeking growth capital without aggressive VC timelines.',
    },
    highlights: {
      revenue: 'N/A',
      customers: '25',
      partnerships: '40+',
      milestones: '50+',
      awards: '3',
      metrics: {
        arrMrr: 'N/A',
        customerCount: '25 portfolio companies',
        retention: '100%',
        growthRate: 'N/A',
        fundingRaised: '$150M AUM',
      },
      funding: {
        totalRaised: 'Family capital',
        currentRaise: 'Evergreen',
        useOfFunds: 'Long-term investments',
      },
    },
    entityType: 'person',
  },

  'entity-sbir-program': {
    snapshot: {
      name: 'SBIR Phase II',
      title: 'Government Grant',
      organization: 'NSF',
      quickBio: 'The Small Business Innovation Research (SBIR) Phase II program provides up to $1M in non-dilutive funding for technology companies developing innovative solutions. Administered by the National Science Foundation, this program supports R&D for technologies with strong commercial potential and societal benefit. Over 500 companies have received Phase II awards.',
      photoUrl: IMAGES.sbirProgram,
    },
    vision: {
      betterPlace: 'A world where breakthrough innovations receive the early funding they need, regardless of short-term commercial viability.',
      aimToChange: 'The funding gap that prevents promising deep-tech innovations from reaching the market.',
      purpose: 'SBIR exists to support American innovation by funding high-risk, high-reward technology development that private capital often overlooks.',
      uniqueApproach: 'We provide non-dilutive funding with no equity stake, allowing founders to develop technology while maintaining full ownership.',
    },
    company: {
      name: 'NSF SBIR Program',
      tagline: 'Non-Dilutive Funding for Innovation',
      industry: 'Government Grants',
      founded: '1982',
      headquarters: 'Washington, DC',
      stage: 'Established',
      coreOffering: 'Phase II grants up to $1M for continued R&D following successful Phase I completion, plus follow-on funding opportunities.',
      targetMarket: 'Small businesses developing innovative technologies with commercial potential and societal benefit.',
    },
    highlights: {
      revenue: 'N/A',
      customers: '500+',
      partnerships: '100+',
      milestones: '1000+',
      awards: 'N/A',
      metrics: {
        arrMrr: 'N/A',
        customerCount: '500+ Phase II awards annually',
        retention: 'N/A',
        growthRate: 'N/A',
        fundingRaised: '$200M+ annually',
      },
      funding: {
        totalRaised: 'Federal funding',
        currentRaise: 'Always open',
        useOfFunds: 'Technology R&D',
      },
    },
    entityType: 'program',
  },

  // ============ COMMUNITY MODEL SECTION ============
  'entity-tech-council': {
    snapshot: {
      name: 'Austin Tech Council',
      title: 'Regional Network',
      organization: null,
      quickBio: 'The Austin Tech Council is the premier membership organization for technology companies in Central Texas. With over 500 member companies, we provide networking events, policy advocacy, talent programs, and investor introductions. Our mission is to make Austin the best place in the world to start and grow a technology company.',
      photoUrl: IMAGES.techCouncil,
    },
    vision: {
      betterPlace: 'A world where vibrant technology ecosystems exist in cities across the country, not just coastal hubs.',
      aimToChange: 'The concentration of technology talent and capital in a few overpriced metros that limits opportunity for founders and workers alike.',
      purpose: 'Austin Tech Council exists to strengthen the Austin technology ecosystem by connecting founders, talent, and capital.',
      uniqueApproach: 'We combine grassroots community building with strategic policy advocacy to create structural advantages for Austin tech companies.',
    },
    company: {
      name: 'Austin Tech Council',
      tagline: 'Powering Austin Tech',
      industry: 'Industry Association',
      founded: '1992',
      headquarters: 'Austin, TX',
      stage: 'Established',
      coreOffering: 'Membership benefits including monthly events, investor introductions, talent programs, group benefits, and policy advocacy.',
      targetMarket: 'Technology companies of all stages based in Central Texas seeking community, resources, and advocacy.',
    },
    highlights: {
      revenue: '$1.5M',
      customers: '500+',
      partnerships: '75+',
      milestones: '100+',
      awards: '5',
      metrics: {
        arrMrr: '$125K MRR',
        customerCount: '500+ member companies',
        retention: '87%',
        growthRate: '20% YoY',
        fundingRaised: 'Member-funded nonprofit',
      },
      funding: {
        totalRaised: 'N/A - Nonprofit',
        currentRaise: 'Ongoing membership',
        useOfFunds: 'Programs and advocacy',
      },
    },
    entityType: 'community',
  },

  'entity-saas-alliance': {
    snapshot: {
      name: 'B2B SaaS Alliance',
      title: 'Industry Group',
      organization: null,
      quickBio: 'B2B SaaS Alliance is an exclusive community of 200 B2B SaaS founders building companies between $1M and $50M ARR. Members share benchmarks, best practices, and referrals through a private Slack community, quarterly dinners, and an annual retreat. Our members have collectively built over $500M in ARR.',
      photoUrl: IMAGES.saasAlliance,
    },
    vision: {
      betterPlace: 'A world where SaaS founders freely share knowledge and support each other, accelerating the entire industry.',
      aimToChange: 'The isolation and information asymmetry that causes SaaS founders to repeat the same mistakes and miss obvious opportunities.',
      purpose: 'B2B SaaS Alliance exists to accelerate member company growth through peer learning and mutual support.',
      uniqueApproach: 'We maintain strict membership criteria and facilitate structured knowledge sharing rather than just networking.',
    },
    company: {
      name: 'B2B SaaS Alliance',
      tagline: 'Learn Faster, Grow Together',
      industry: 'Professional Community',
      founded: '2019',
      headquarters: 'Virtual (US)',
      stage: 'Growth',
      coreOffering: 'Membership includes private Slack, benchmark data, quarterly dinners, annual retreat, and curated introductions.',
      targetMarket: 'B2B SaaS founders with $1M-$50M ARR seeking peer learning and community.',
    },
    highlights: {
      revenue: '$480K',
      customers: '200',
      partnerships: '15',
      milestones: '20',
      awards: '2',
      metrics: {
        arrMrr: '$40K MRR',
        customerCount: '200 members',
        retention: '94%',
        growthRate: '40% YoY',
        fundingRaised: 'Member-funded',
      },
      funding: {
        totalRaised: 'N/A',
        currentRaise: 'N/A',
        useOfFunds: 'Community programs',
      },
    },
    entityType: 'community',
  },

  'entity-enterprise-partners': {
    snapshot: {
      name: 'Enterprise Connect',
      title: 'Channel Partnership',
      organization: null,
      quickBio: 'Enterprise Connect is a value-added reseller (VAR) network that helps technology startups access enterprise customers. With established relationships at over 50 Fortune 500 companies, we provide warm introductions, co-selling support, and procurement navigation. Our partners have closed over $100M in enterprise deals.',
      photoUrl: IMAGES.enterpriseConnect,
    },
    vision: {
      betterPlace: 'A world where innovative startups can compete for enterprise business on the merits of their solutions, not their Rolodex.',
      aimToChange: 'The unfair advantage that incumbent vendors have due to existing relationships rather than superior products.',
      purpose: 'Enterprise Connect exists to level the playing field for startups selling to enterprises.',
      uniqueApproach: 'We combine warm introductions with hands-on sales support through the entire enterprise procurement process.',
    },
    company: {
      name: 'Enterprise Connect',
      tagline: 'Your Path to Enterprise Customers',
      industry: 'Channel Sales',
      founded: '2018',
      headquarters: 'New York, NY',
      stage: 'Established',
      coreOffering: 'Partner program including warm introductions, co-selling support, RFP assistance, and procurement navigation.',
      targetMarket: 'B2B software companies with enterprise-ready products seeking to break into Fortune 500 accounts.',
    },
    highlights: {
      revenue: '$3.2M',
      customers: '45',
      partnerships: '50+',
      milestones: '200+',
      awards: '3',
      metrics: {
        arrMrr: 'Deal-based',
        customerCount: '45 active partners',
        retention: '88%',
        growthRate: '50% YoY',
        fundingRaised: '$5M Series A',
      },
      funding: {
        totalRaised: '$5M Series A',
        currentRaise: 'Not raising',
        useOfFunds: 'Enterprise relationship expansion',
      },
    },
    entityType: 'organization',
  },

  'entity-university-lab': {
    snapshot: {
      name: 'UT Innovation Lab',
      title: 'Research Partnership',
      organization: 'University of Texas',
      quickBio: 'The UT Innovation Lab connects startups with world-class research capabilities at the University of Texas at Austin. We provide access to faculty expertise, graduate student talent, research facilities, and grant opportunities. Over 100 startups have partnered with us to advance their R&D efforts.',
      photoUrl: IMAGES.utInnovation,
    },
    vision: {
      betterPlace: 'A world where university research quickly translates into real-world impact through close collaboration with innovative companies.',
      aimToChange: 'The ivory tower mentality that keeps valuable research locked away from commercial application.',
      purpose: 'UT Innovation Lab exists to bridge the gap between academic research and commercial innovation.',
      uniqueApproach: 'We provide startup-friendly terms and fast-track processes that respect the pace of business, not just academic timelines.',
    },
    company: {
      name: 'UT Innovation Lab',
      tagline: 'Research Partnerships for Innovation',
      industry: 'University Research',
      founded: '2015',
      headquarters: 'Austin, TX',
      stage: 'Established',
      coreOffering: 'Sponsored research agreements, faculty consulting, graduate internships, facility access, and grant collaboration.',
      targetMarket: 'Technology startups seeking R&D capabilities, specialized expertise, or credibility through academic partnership.',
    },
    highlights: {
      revenue: '$4.5M',
      customers: '100+',
      partnerships: '25',
      milestones: '50+',
      awards: '8',
      metrics: {
        arrMrr: 'Project-based',
        customerCount: '100+ startup partners',
        retention: '82%',
        growthRate: '25% YoY',
        fundingRaised: 'University-funded',
      },
      funding: {
        totalRaised: 'University budget',
        currentRaise: 'N/A',
        useOfFunds: 'Research and programs',
      },
    },
    entityType: 'organization',
  },
};

// Section configuration with styling
export const SECTION_CONFIG = {
  capacity: {
    id: 'capacity',
    title: 'Capacity',
    subtitle: 'People and resources to help you scale',
    color: '#23A38F', // teal
    icon: 'GroupsIcon',
  },
  care: {
    id: 'care',
    title: 'Care',
    subtitle: 'Wellness and personal development resources',
    color: '#9C27B0', // purple
    icon: 'FavoriteIcon',
  },
  capital: {
    id: 'capital',
    title: 'Capital',
    subtitle: 'Funding and financial resources',
    color: '#D8A200', // gold
    icon: 'AccountBalanceIcon',
  },
  communityModel: {
    id: 'communityModel',
    title: 'Community Model',
    subtitle: 'Networks and strategic partnerships',
    color: '#0097B2', // blue
    icon: 'HubIcon',
  },
};

// Entity type display configuration
export const ENTITY_TYPE_CONFIG = {
  person: {
    label: 'Person',
    icon: 'PersonIcon',
    color: '#23A38F',    // Teal
  },
  organization: {
    label: 'Organization',
    icon: 'BusinessIcon',
    color: '#141B2D',    // Ink
  },
  program: {
    label: 'Program',
    icon: 'SchoolIcon',
    color: '#00BCD4',    // Accent blue
  },
  community: {
    label: 'Community',
    icon: 'GroupsIcon',
    color: '#D8A200',    // Gold
  },
};
